import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { useHumanAgentsQueryVariables } from './useHumanAgentsQueryVariables';
import type { TimeseriesDataPoint } from './useOverviewTimeseries';
import type {
  FeedbackChartKey,
  FeedbackMetricKey,
} from '../components/FeedbackOverview/FeedbackOverviewCard';
import type {
  UserCommentRow,
  UserCommentsSortKey,
} from '../components/UserComments/UserCommentsTable';

interface Params {
  sort: { key: UserCommentsSortKey; direction: 'asc' | 'desc' };
  pageIndex: number;
  pageSize: number;
  withComments: boolean;
}

interface SummaryRow {
  avg_user_csat: number | null;
  rated_conversations: number | null;
  comments_sent: number | null;
}

interface TimeseriesRow {
  time_bucket: string;
  avg_user_csat: number | null;
  total_reviews: number;
}

interface CommentRow {
  session_id: string;
  comment_time: string;
  user_name: string | null;
  comment: string;
  rating: number;
  brain_parent_id: string | null;
}

const FILTER_VARIABLES = `
  $start_time: timestamp!
  $end_time: timestamp!
  $is_test: Boolean
`;

const FILTER_ARGS = `
  start_time: $start_time
  end_time: $end_time
  is_test: $is_test
`;

export const GET_USER_FEEDBACK_SUMMARY = gql`
  query UserFeedbackSummary(${FILTER_VARIABLES}) {
    rows: user_feedback_summary(args: { ${FILTER_ARGS} }) {
      avg_user_csat
      rated_conversations
      comments_sent
    }
  }
`;

export const GET_USER_FEEDBACK_TIMESERIES = gql`
  query UserFeedbackTimeseries(${FILTER_VARIABLES}) {
    rows: user_feedback_timeseries(args: { ${FILTER_ARGS} }) {
      time_bucket
      avg_user_csat
      total_reviews
    }
  }
`;

export const GET_USER_FEEDBACK_COMMENTS = gql`
  query UserFeedbackComments(
    ${FILTER_VARIABLES}
    $order_by: String
    $sort_direction: String
    $row_limit: Int
    $row_offset: Int
  ) {
    rows: user_feedback_comments(
      args: {
        ${FILTER_ARGS}
        order_by: $order_by
        sort_direction: $sort_direction
        row_limit: $row_limit
        row_offset: $row_offset
      }
    ) {
      session_id
      comment_time
      user_name
      comment
      rating
      brain_parent_id
    }
  }
`;

// Postgres TIMESTAMP comes back without a zone; the value is UTC.
const asUtc = (value: string) => (/Z|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);

const toRow = (row: CommentRow): UserCommentRow => ({
  id: row.session_id,
  sessionId: row.session_id,
  user: row.user_name ?? '',
  comment: row.comment,
  csat: row.rating,
  createdAt: asUtc(row.comment_time),
  brainParentId: row.brain_parent_id ?? undefined,
});

export const useUserFeedback = ({ sort, pageIndex, pageSize, withComments }: Params) => {
  const { variables: all, skip } = useHumanAgentsQueryVariables();
  // The page has no filters: only the date range, and real conversations only.
  const variables = { start_time: all.start_time, end_time: all.end_time, is_test: false };

  const summary = useQuery<{ rows: SummaryRow[] }>(GET_USER_FEEDBACK_SUMMARY, { variables, skip });
  const timeseries = useQuery<{ rows: TimeseriesRow[] }>(GET_USER_FEEDBACK_TIMESERIES, {
    variables,
    skip,
  });
  const comments = useQuery<{ rows: CommentRow[] }>(GET_USER_FEEDBACK_COMMENTS, {
    variables: {
      ...variables,
      order_by: sort.key,
      sort_direction: sort.direction,
      row_limit: pageSize,
      row_offset: pageIndex * pageSize,
    },
    skip: skip || !withComments,
  });
  // refetch() ignores skip, so a skipped comments query must stay out of the list.
  const queries = withComments ? [summary, timeseries, comments] : [summary, timeseries];

  const s = summary.data?.rows?.[0];
  const totals: Record<FeedbackMetricKey, number | null> = {
    avgCsat: s?.avg_user_csat ?? null,
    ratedConversations: s?.rated_conversations ?? null,
    commentsSent: s?.comments_sent ?? null,
  };

  const days = timeseries.data?.rows ?? [];
  // Keep the UTC day: padSeriesToPeriod matches on it, and a local-time format shifts it west of UTC.
  const series: Record<FeedbackChartKey, TimeseriesDataPoint[]> = {
    avgCsat: days.map((row) => ({
      date: row.time_bucket.slice(0, 10),
      value: Number((row.avg_user_csat ?? 0).toFixed(2)),
      reviewCount: row.total_reviews,
    })),
    ratedConversations: days.map((row) => ({
      date: row.time_bucket.slice(0, 10),
      value: row.total_reviews,
    })),
  };

  return {
    totals,
    series,
    rows: (comments.data?.rows ?? []).map(toRow),
    total: totals.commentsSent ?? 0,
    isLoading: summary.loading || timeseries.loading,
    isLoadingRows: comments.loading,
    error: summary.error ?? timeseries.error ?? comments.error,
    refetch: () => queries.forEach((query) => query.refetch()),
  };
};
